<template>
  <NuxtLayout name="auth">
    <div class="fixed inset-0 z-0 overflow-hidden pointer-events-none">
      <div
        class="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-brand/10 blur-[120px] rounded-full"
      />
      <div
        class="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-brand/5 blur-[120px] rounded-full"
      />
    </div>

    <div class="relative z-10 w-full max-w-lg">
      <div class="flex flex-col items-center mb-10 text-center">
        <PebbleAppHomeLogo class="h-10 mb-6" />
        <h1 class="text-4xl font-black text-white tracking-tighter mb-2">
          Lost <span class="text-brand">Access?</span>
        </h1>
        <p class="text-text-weak-dark text-[10px] font-black tracking-[0.3em]">
          Let’s help you recover your account
        </p>
      </div>

      <div
        class="glass-heavy border border-white/10 rounded-[2.5rem] p-8 lg:p-12 shadow-2xl relative overflow-hidden"
      >
        <div
          class="absolute top-0 inset-x-0 h-px bg-linear-to-r from-transparent via-white/20 to-transparent"
        />

        <div class="flex justify-center mb-8">
          <div class="relative w-16 h-16">
            <div
              class="absolute inset-0 bg-brand/20 blur-lg rounded-full animate-pulse"
            />
            <div
              class="relative w-full h-full glass-premium rounded-2xl flex items-center justify-center border border-brand/30"
            >
              <PebbleAppIconsThought class="text-brand w-8 h-8" />
            </div>
          </div>
        </div>

        <p
          class="text-center text-[11px] font-bold text-text-weak-dark tracking-widest mb-10 leading-relaxed px-4"
        >
          Choose where we should send your reset details.
        </p>

        <div class="mb-10">
          <PebbleAppTabs v-model:active-tab="activeTab" :tabs="tabs" />
        </div>

        <form
          v-if="activeTab === 'email'"
          @submit.prevent="onEmailSubmit"
          class="space-y-8"
        >
          <PebbleAppFormInput
            v-model="emailValue"
            label="Email address"
            placeholder="Enter your email"
            type="email"
            :error="emailErrorMessage"
            :disabled="isLoading"
          >
            <template #leftIcon>
              <Mail class="size-4 opacity-40" />
            </template>
          </PebbleAppFormInput>

          <PebbleAppLink
            type="submit"
            variant="primary"
            size="lg"
            :leading-icon="Send"
            :full-width="true"
            :loading="isLoading"
          >
            Send reset link
          </PebbleAppLink>
        </form>

        <form v-else @submit.prevent="onMobileSubmit" class="space-y-8">
          <PebbleAppFormInput
            v-model="mobileValue"
            v-model:country-code="selectedCountryCode"
            label="Phone number"
            placeholder="000 000 000"
            is-mobile-field
            :error="mobileErrorMessage"
            :disabled="isLoading"
          />

          <PebbleAppLink
            type="submit"
            variant="primary"
            size="lg"
            :leading-icon="Send"
            :full-width="true"
            :loading="isLoading"
          >
            Send reset link
          </PebbleAppLink>
        </form>
      </div>

      <div class="mt-10 text-center">
        <NuxtLink
          to="/login"
          class="text-[10px] font-black text-text-weak-dark hover:text-white tracking-[0.2em] transition-all flex items-center justify-center gap-2"
        >
          <ArrowLeft class="size-3" /> Back to login
        </NuxtLink>
      </div>
    </div>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import { ref, watch, computed } from "vue";
import { useForm, useField } from "vee-validate";
import { Send, Mail, ArrowLeft } from "lucide-vue-next";
import {
  forgotPasswordWithEmailSchema,
  forgotPasswordWithMobileSchema,
} from "@/utils/validations/forgot-password";
import { useNuxtServerApi } from "~/composables/http/useNuxtServerAPI";
import { OTPVerificationType } from "#shared/enums";

usePageTitle({
  title: "Recover Identity",
  description: "Initiate recovery for your Pebble account",
});

const tabs = [
  { id: "email", label: "Email" },
  { id: "mobile", label: "Mobile" },
];

const $nuxtApi = useNuxtServerApi();
const { showSuccessToast } = useNotification();

const activeTab = ref<string>("email");
const selectedCountryCode = ref("+233");
const isLoading = ref(false);

const { handleSubmit } = useForm({
  validationSchema: computed(() =>
    activeTab.value === "email"
      ? forgotPasswordWithEmailSchema()
      : forgotPasswordWithMobileSchema(selectedCountryCode),
  ),
});

const { value: emailValue, errorMessage: emailErrorMessage } =
  useField<string>("email");

const { value: mobileValue, errorMessage: mobileErrorMessage } =
  useField<string>("mobile");

watch(mobileValue, (newVal) => {
  if (newVal?.startsWith("0")) {
    mobileValue.value = newVal.substring(1);
  }
});

const onEmailSubmit = handleSubmit(async () => {
  await submitForgotPassword(emailValue.value);
});

const onMobileSubmit = handleSubmit(async () => {
  const fullMobile = `${selectedCountryCode.value}${mobileValue.value.replace(/^0+/, "")}`;
  await submitForgotPassword(fullMobile);
});

async function submitForgotPassword(identifier: string) {
  if (isLoading.value) return;
  isLoading.value = true;

  try {
    const response = await $nuxtApi.post("auth/forgot-password", {
      identifier,
      method: activeTab.value,
    });

    if (statusOk(response.status)) {
      showSuccessToast(
        "Transmission Successful",
        "Recovery node has been notified.",
      );
      navigateTo({
        path: "/otp-verification",
        query: { identifier, type: OTPVerificationType.PASSWORD_RESET },
      });
    }
  } catch (err) {
    console.error("Submission Error:", err);
  } finally {
    isLoading.value = false;
  }
}
</script>

<style scoped>
.glass-heavy {
  background: rgba(15, 15, 17, 0.85);
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
}

.glass-premium {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(12px);
}
</style>
